/**
 * TierDetailPanel - Tabbed detail panel for a selected knowledge tier
 *
 * Sub-tabs: Overview | Your Metrics | Examples | FAIR Impact
 */

import React, { useState } from 'react';
import type {
  KnowledgeTier,
  TierDetectionResult,
  FairDimensionScores,
  OntologyStatistics,
  StructuralAnalysisData,
} from './types';
import { TIER_INFO, TIER_COLORS } from './types';
import { FairDimensionBars } from './FairDimensionBars';

interface TierDetailPanelProps {
  selectedTier: KnowledgeTier;
  detection: TierDetectionResult;
  fairScores: FairDimensionScores;
  statistics: OntologyStatistics;
  structural: StructuralAnalysisData | null;
  structuralLoading: boolean;
  onSelectTier: (tier: KnowledgeTier) => void;
}

type DetailTab = 'overview' | 'metrics' | 'examples' | 'fair';

export const TierDetailPanel: React.FC<TierDetailPanelProps> = ({
  selectedTier,
  detection,
  fairScores,
  statistics,
  structural,
  structuralLoading,
  onSelectTier,
}) => {
  const [activeDetailTab, setActiveDetailTab] = useState<DetailTab>('overview');
  const info = TIER_INFO[selectedTier];
  const isDetected = selectedTier === detection.tier;
  const color = TIER_COLORS[selectedTier];

  const detailTabs: Array<{ key: DetailTab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'metrics', label: 'Your Metrics' },
    { key: 'examples', label: 'Examples' },
    { key: 'fair', label: 'FAIR Impact' },
  ];

  return (
    <div className="ka-detail-panel">
      <div className="ka-detail-header" style={{ borderLeftColor: color }}>
        <div className="ka-detail-title-row">
          <h3 className="ka-detail-title" style={{ color }}>
            {info.label}
          </h3>
          {isDetected && (
            <span className="ka-badge ka-badge-detected" style={{ backgroundColor: `${color}30`, color }}>
              Your ontology
            </span>
          )}
        </div>
        <p className="ka-detail-definition">{info.definition}</p>
      </div>

      {/* Detail sub-tabs */}
      <div className="ka-detail-tabs">
        {detailTabs.map(tab => (
          <button
            key={tab.key}
            className={`ka-detail-tab ${activeDetailTab === tab.key ? 'ka-detail-tab-active' : ''}`}
            onClick={() => setActiveDetailTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="ka-detail-content">
        {activeDetailTab === 'overview' && (
          <OverviewContent
            info={info}
            isDetected={isDetected}
            detection={detection}
            selectedTier={selectedTier}
            onSelectTier={onSelectTier}
          />
        )}
        {activeDetailTab === 'metrics' && (
          <MetricsContent
            selectedTier={selectedTier}
            detection={detection}
            statistics={statistics}
            structural={structural}
            loading={structuralLoading}
          />
        )}
        {activeDetailTab === 'examples' && (
          <ExamplesContent info={info} color={color} />
        )}
        {activeDetailTab === 'fair' && (
          <FairImpactContent
            info={info}
            fairScores={fairScores}
            structuralLoading={structuralLoading}
          />
        )}
      </div>
    </div>
  );
};

// =============================================================================
// SUB-TAB CONTENT COMPONENTS
// =============================================================================

const OverviewContent: React.FC<{
  info: typeof TIER_INFO[KnowledgeTier];
  isDetected: boolean;
  detection: TierDetectionResult;
  selectedTier: KnowledgeTier;
  onSelectTier: (tier: KnowledgeTier) => void;
}> = ({ info, isDetected, detection, selectedTier, onSelectTier }) => {
  const TIER_ORDER: KnowledgeTier[] = ['taxonomy', 'ontology', 'knowledge_graph'];
  const tierIdx = TIER_ORDER.indexOf(selectedTier);

  return (
    <div className="ka-overview">
      <div className="ka-overview-section">
        <h5 className="ka-label">DATA Compass Role</h5>
        <p className="ka-text">{info.compassRole}</p>
      </div>

      <div className="ka-overview-section">
        <h5 className="ka-label">Capabilities</h5>
        <div className="ka-capability-badges">
          {info.capabilities.map(cap => (
            <span key={cap} className="ka-capability-badge">{cap}</span>
          ))}
        </div>
      </div>

      {isDetected && detection.nextTierActions.length > 0 && (
        <div className="ka-overview-section">
          <h5 className="ka-label">Next Steps to Advance</h5>
          <ul className="ka-action-list">
            {detection.nextTierActions.map((action, i) => (
              <li key={i} className="ka-action-item">{action}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Navigation to other tiers */}
      <div className="ka-overview-section">
        <h5 className="ka-label">Builds On</h5>
        <div className="ka-tier-links">
          {tierIdx > 0 && (
            <button
              className="ka-tier-link"
              onClick={() => onSelectTier(TIER_ORDER[tierIdx - 1])}
              style={{ color: TIER_COLORS[TIER_ORDER[tierIdx - 1]] }}
            >
              {TIER_INFO[TIER_ORDER[tierIdx - 1]].label}
            </button>
          )}
          {tierIdx < TIER_ORDER.length - 1 && (
            <button
              className="ka-tier-link"
              onClick={() => onSelectTier(TIER_ORDER[tierIdx + 1])}
              style={{ color: TIER_COLORS[TIER_ORDER[tierIdx + 1]] }}
            >
              {TIER_INFO[TIER_ORDER[tierIdx + 1]].label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface MetricRow {
  label: string;
  value: string;
  status: 'achieved' | 'gap' | 'neutral';
}

const MetricsContent: React.FC<{
  selectedTier: KnowledgeTier;
  detection: TierDetectionResult;
  statistics: OntologyStatistics;
  structural: StructuralAnalysisData | null;
  loading: boolean;
}> = ({ selectedTier, statistics, structural, loading }) => {
  if (loading) {
    return (
      <div className="ka-metrics-loading">
        <div className="spinner-small" />
        <span>Loading structural metrics...</span>
      </div>
    );
  }

  const metrics: MetricRow[] = buildMetrics(selectedTier, statistics, structural);

  return (
    <div className="ka-metrics">
      <p className="ka-metrics-note">
        Real values from your uploaded ontology
      </p>
      <div className="ka-metrics-grid">
        {metrics.map(m => (
          <div key={m.label} className={`ka-metric-card ka-metric-${m.status}`}>
            <span className="ka-metric-label">{m.label}</span>
            <span className="ka-metric-value">{m.value}</span>
            <span className={`ka-metric-status ka-status-${m.status}`}>
              {m.status === 'achieved' ? 'Achieved' : m.status === 'gap' ? 'Gap' : '--'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

function buildMetrics(
  tier: KnowledgeTier,
  stats: OntologyStatistics,
  structural: StructuralAnalysisData | null
): MetricRow[] {
  const metrics: MetricRow[] = [];

  // Common metrics
  metrics.push({
    label: 'Classes',
    value: stats.class_count.toString(),
    status: stats.class_count > 0 ? 'achieved' : 'gap',
  });

  metrics.push({
    label: 'Hierarchy Depth',
    value: stats.hierarchy_depth.toString(),
    status: stats.hierarchy_depth >= 2 ? 'achieved' : stats.hierarchy_depth >= 1 ? 'neutral' : 'gap',
  });

  if (tier === 'ontology' || tier === 'knowledge_graph') {
    metrics.push({
      label: 'Properties',
      value: stats.property_count.toString(),
      status: stats.property_count > 0 ? 'achieved' : 'gap',
    });

    const domainPct = structural?.propertiesWithDomain?.percentage ?? 0;
    metrics.push({
      label: 'Domain Coverage',
      value: `${domainPct.toFixed(0)}%`,
      status: domainPct >= 50 ? 'achieved' : domainPct >= 20 ? 'neutral' : 'gap',
    });

    const rangePct = structural?.propertiesWithRange?.percentage ?? 0;
    metrics.push({
      label: 'Range Coverage',
      value: `${rangePct.toFixed(0)}%`,
      status: rangePct >= 50 ? 'achieved' : rangePct >= 20 ? 'neutral' : 'gap',
    });

    const shaclIssues = (structural?.shaclConsistency?.undeclaredProperties?.length ?? 0)
      + (structural?.shaclConsistency?.phantomClasses?.length ?? 0);
    metrics.push({
      label: 'SHACL Issues',
      value: shaclIssues.toString(),
      status: shaclIssues === 0 ? 'achieved' : 'gap',
    });
  }

  if (tier === 'knowledge_graph') {
    metrics.push({
      label: 'Individuals',
      value: stats.individual_count.toString(),
      status: stats.individual_count > 0 ? 'achieved' : 'gap',
    });

    const ratio = stats.class_count > 0 ? stats.individual_count / stats.class_count : 0;
    metrics.push({
      label: 'Individual/Class Ratio',
      value: ratio.toFixed(2),
      status: ratio >= 0.1 ? 'achieved' : 'gap',
    });
  }

  // Annotation coverage for all tiers
  const labelPct = structural?.annotationCoverage?.rdfsLabel?.percentage ?? 0;
  metrics.push({
    label: 'Label Coverage',
    value: `${labelPct.toFixed(0)}%`,
    status: labelPct >= 80 ? 'achieved' : labelPct >= 50 ? 'neutral' : 'gap',
  });

  return metrics;
}

const ExamplesContent: React.FC<{
  info: typeof TIER_INFO[KnowledgeTier];
  color: string;
}> = ({ info, color }) => {
  return (
    <div className="ka-examples">
      <h5 className="ka-label">Pharma Industry Examples</h5>
      <div className="ka-example-list">
        {info.pharmaExamples.map((example, i) => (
          <div key={i} className="ka-example-item" style={{ borderLeftColor: color }}>
            {example}
          </div>
        ))}
      </div>
    </div>
  );
};

const FairImpactContent: React.FC<{
  info: typeof TIER_INFO[KnowledgeTier];
  fairScores: FairDimensionScores;
  structuralLoading: boolean;
}> = ({ info, fairScores, structuralLoading }) => {
  return (
    <div className="ka-fair-impact">
      <div className="ka-overview-section">
        <h5 className="ka-label">Impact at This Tier</h5>
        <p className="ka-text">{info.fairImpact}</p>
      </div>
      <FairDimensionBars scores={fairScores} loading={structuralLoading} />
    </div>
  );
};
